/*
 | ======================================================
 | This File is for member hub vault API services handling
 | ======================================================
 */
import { ApiService } from './api.service'

class VaultService {
  constructor () {
    this.request = new ApiService()
  }

  /*
     * Vault save API service
     * @param formData
     * @returns {*}
     */
  save = formData => {
    return this.request.post('/vaults', formData)
  }

  /*
     * Vault List API service
     * @returns {*}
     */
  list = () => {
    return this.request.get('/vaults')
  }

  /*
     * Vault product delete API service
     * @param productId
     * @returns {*}
     */
  delete = productId => {
    return this.request.delete(`/vaults/${productId}`)
  }

  /*
     * vault product details API service
     * @param productId
     * @returns {*}
     */
  show = productId => {
    return this.request.get(`/vaults/${productId}`, { canCancel: true })
  }

  /*
     * vault product update API service
     * @param productId
     * @param formData
     * @returns {*}
     */
  update = (productId, formData) => {
    return this.request.patch(`/vaults/${productId}`, formData)
  }

  /*
     * Vault product rebate update API service
     * @param productId
     * @param formData
     * @returns {*}
     */
  rebate = (productId, formData) => {
    return this.request.patch(`/vault-rebate/${productId}`, formData)
  }

  /*
   * Policies dropdown for vault API service
   * @param url
   * @returns {*}
   */
  policies = url => {
    return this.request.get(url)
  }

  /*
   * Amply policy details API service
   * @param policyProductId
   * @returns {*}
   */
  policyDetails = policyProductId => {
    return this.request.get(`/data/health-insurance/policies/${policyProductId}`)
  }

  /*
   * Validate purchased user API service
   * @param email
   * @returns {*}
   */
  validateUser = payload => {
    return this.request.get('/validate-purchased-user', { params: payload })
  }

  /*
   * Purchased user signup API service
   * @param formData
   * @returns {*}
   */
  purchasedUserSignup = formData => {
    return this.request.post('/purchased-user-signup', formData)
  }

  /*
   * Purchase Sync API service
   * @returns {*}
   */
  purchaseSync = () => {
    return this.request.get('/vault-sync')
  }

  /*
   * Create lead API service
   * @param vaultId
   * @param params
   * @returns {*}
   */
  createLead = (vaultId, params = {}) => {
    return this.request.get(`/vaults/${vaultId}/lead`, { params })
  }

  /*
   * Save file(s) API service
   * @param vaultId
   * @param files
   * @returns {*}
   */
  saveFiles = (vaultId, files) => {
    return this.request.post(`/vaults/${vaultId}/files`, files)
  }

  /*
   * Rename file API service
   * @param vaultId
   * @param fileId
   * @param formData
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | null, FetchError | null>}
   */
  renameFile = (vaultId, fileId, formData) => {
    return this.request.patch(`/vaults/${vaultId}/files/${fileId}/rename`, formData)
  }

  /*
   * Delete file API service
   * @param vaultId
   * @param fileId
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | null, FetchError | null>}
   */
  deleteFile = (vaultId, fileId) => {
    return this.request.delete(`/vaults/${vaultId}/files/${fileId}`)
  }

  /*
   * Archive vault API service
   * @param vaultId
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | null, FetchError | null>}
   */
  archive = vaultId => {
    return this.request.post(`/vaults/${vaultId}/archive`)
  }

  /*
   * Restore archived vault API service
   * @param vaultId
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | null, FetchError | null>}
   */
  restore = vaultId => {
    return this.request.post(`/vaults/${vaultId}/restore`)
  }

  /*
   * Archive vault List API service
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | null, FetchError | null>}
   */
  archiveList = () => {
    return this.request.get('/vaults/archives')
  }

  /*
   * Vault LIC product details API service
   * @param apiUrl
   * @param opportunityId
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | null, FetchError | null>}
   */
  licDetails = (apiUrl, opportunityId) => {
    const { apikeyLifeInsurance } = useRuntimeConfig().public
    return this.request.get(apiUrl, { params: { opportunity_id: opportunityId, apikey: apikeyLifeInsurance } })
  }

  /*
   * Vault Health policies list API service (New Version)
   * @param payload
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | DefaultAsyncDataValue, DefaultAsyncDataErrorValue | FetchError>}
   */
  healthInsurancePolicies (payload) {
    return this.request.post('/data/health-insurance/policies-v2' , payload)
  }

  /*
   * Vault Health policies duplicate check API service
   * @param payload
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | DefaultAsyncDataValue, DefaultAsyncDataErrorValue | FetchError>}
   */
  duplicateHealthInsurancePolicies (payload) {
    return this.request.post('/data/health-insurance/policies-v2-duplicate-check' , payload)
  }
  vendors = params => {
    return this.request.get('/data/vendors', { params })
  }
  /*
   * Vault Rate rise confirm API service
   * @param vaultId
   * @param formData
   * @returns {AsyncData<PickFrom<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>, KeysOf<FetchResult<NitroFetchRequest, "get" extends AvailableRouterMethod<NitroFetchRequest> ? "get" : AvailableRouterMethod<NitroFetchRequest>>>> | DefaultAsyncDataValue, DefaultAsyncDataErrorValue | FetchError>}
   */
  rateRiseConfirm = (vaultId, formData) => {
    return this.request.post(`/vaults/${vaultId}/rate-rise-confirm`, formData)
  }

  /*
   * Confirms a health draft vault.
   * @param vaultId
   * @returns {*}
   */
  confirmHealthDraft = vaultId => {
    return this.request.patch(`/vaults/${vaultId}/confirm-draft`, {})
  }
  /*
 * Matches the address using the provided payload.
 *
 * @param {Object} payload - The payload containing address data to match.
 * @returns {Promise} - A promise that resolves with the response data or rejects with an error.
 */
  addressMatch = payload => {
    return this.request.post('/vault-address-match', payload, { canCancel: true })
  }

  /*
 * Updates the lead ID for a specific vault.
 *
 * @param {string} vaultId - The ID of the vault to update.
 * @param {string} uuid - The UUID to update the lead ID with.
 * @returns {Promise} - A promise that resolves with the response data or rejects with an error.
 */
  updateLeadId = (vaultId, uuid) => {
    return this.request.patch(`/vaults/${vaultId}/lead/${uuid}`)
  }
  /*
 * Fetches the list of verticals from the API.
 *
 * @returns {Promise} - A promise that resolves to the response of the API call.
 */
  verticals = () => {
    return this.request.get('/data/verticals', { canCancel: true })
  }

  /*
   * Amply policy details combine API service
   * @param payload
   * @returns {*}
   */
  policyDetailsCombine = payload => {
    return this.request.get('/data/health-insurance/policies/combine', { params: payload })
  }
  /*
   * Fetches the details of a purchased product from the vault.
   *
   * @param {string} uuid - The unique identifier of the purchased product.
   * @returns {Promise} - A promise that resolves with the product details.
   */
  purchasedProductDetails = uuid => {
    return this.request.get(`/data/vault/${uuid}`)
  }

  /*
   * Get Confidence Score for specific vault API service
   * @param vault_id
   * @returns {*}
   */
  getConfidenceScore = vault_id => {
    return this.request.get(`/vaults/${vault_id}/score`)
  }

  /*
   * Unlock review status for a purchased health insurance vault product.
   * TODO: Replace endpoint path once the API endpoint is finalised.
   * @param vaultId
   * @returns {*}
   */
  unlockReviewStatus = vaultId => {
    return this.request.post(`/vaults/${vaultId}/review-status/optin`)
  }

  /*
   * Forwarded bills list API service
   * Fetches bills forwarded/imported via email for the member hub vault.
   * @returns {*}
   */
  forwardedBills = () => {
    return this.request.get('/forwarded-bills')
  }

  /*
   * Imported bills list API service
   * Fetches the member's draft vaults: bills found by scanning their connected
   * inboxes, across all connected accounts, one draft per bill (each names the
   * account it came from). Call it once the processing page sees the sync
   * complete; an empty list means the scan found nothing.
   * @param {string} [accountId] - connected account from the Google callback;
   *   scopes the drafts to that account
   * @returns {*}
   */
  importedBills = (accountId = '') => {
    return accountId
      ? this.request.get('/vaults/drafts', { params: { account_id: accountId } })
      : this.request.get('/vaults/drafts')
  }

  /*
   * Energy bill history API service (CLUB-480 Phase 2)
   * The last 15 bills for one confirmed energy vault, newest first, with
   * summary fields only (id, billed_date, amount, amount_period). Used by
   * Bills History on energy product details. Shapes:
   * tests/tests/fixtures/energyBillHistory.fixture.ts.
   * @param vaultId - vault product uuid or id
   * @returns {*}
   */
  billHistory = vaultId => {
    return this.request.get(`/vaults/${vaultId}/bills`)
  }

  /*
   * Energy bill detail API service (CLUB-480 Phase 2)
   * One bill's full detail: billing period (with a server-formatted label),
   * next payment date, usage, source document (with its caption) and
   * navigation. Used by bill details on energy product details.
   * @param vaultId - vault product uuid or id
   * @param billId - bill-history entry id from billHistory()
   * @returns {*}
   */
  billDetail = (vaultId, billId) => {
    return this.request.get(`/vaults/${vaultId}/bills/${billId}`)
  }

  /*
   * Energy CDR consent API service
   * The CDR consent behind one energy vault; empty when the vault has none.
   * Tells product details the vault's bills come from CDR.
   * @param vaultId - vault product uuid or id
   * @returns {*}
   */
  cdrConsent = vaultId => {
    return this.request.get(`/vaults/${vaultId}/cdr-consent`)
  }

  /*
   * Energy CDR bill history API service
   * The CDR invoices for one energy vault, shown in Bills History.
   * @param vaultId - vault product uuid or id
   * @returns {*}
   */
  cdrBills = vaultId => {
    return this.request.get(`/vaults/${vaultId}/cdr-bills`)
  }

  /*
   * Energy CDR bill detail API service
   * One CDR invoice's full detail, shown in bill details.
   * @param vaultId - vault product uuid or id
   * @param invoiceNumber - invoice number from cdrBills()
   * @returns {*}
   */
  cdrBill = (vaultId, invoiceNumber) => {
    return this.request.get(`/vaults/${vaultId}/cdr-bills/${encodeURIComponent(invoiceNumber)}`)
  }
}

export default VaultService
